import {
  cameraToViewport,
  clampZoom,
  lerpCamera,
  MIN_ZOOM,
  screenToBoard,
  viewportToCamera,
  type Camera,
  type Point,
  type Rect,
  type ViewportSize,
} from '@praesto/shared'

/**
 * Ruhezeit nach der letzten Zoomänderung, nach der die Ebene wieder scharf
 * gerastert wird.
 *
 * Lang genug, dass die Pause zwischen zwei Mausrad-Rasten keine Neurasterung
 * mitten in der Geste auslöst; kurz genug, dass niemand auf ein scharfes Bild
 * wartet.
 */
const RESHARPEN_DELAY_MS = 160

/**
 * Kamera-Zustand außerhalb von React.
 *
 * Kernidee: Zoomen und Pannen dürfen **keinen** React-Render auslösen. Die
 * Kamera schreibt ihre Transform direkt auf den Element-Layer — der Compositor
 * skaliert dann die gesamte Ebene auf der GPU, ohne dass ein einziges Element
 * neu layoutet wird. Genau daher kommt das "butterweiche" Zoomen.
 *
 * Komponenten, die auf die Kamera reagieren *müssen* (LOD-Auswahl, Culling),
 * abonnieren über `useSyncExternalStore` und rendern nur dann neu, wenn sich
 * ihr abgeleiteter Wert — die Auflösungsstufe, das Sichtbarkeits-Flag —
 * tatsächlich ändert.
 */
export class CameraStore {
  private camera: Camera = { x: 0, y: 0, zoom: 1 }
  private size: ViewportSize = { width: 1, height: 1 }
  private layer: HTMLElement | null = null
  private listeners = new Set<() => void>()
  private notifyScheduled = false
  private animation: number | null = null
  /**
   * Zielrechteck, auf das die Kamera zuletzt eingepasst wurde.
   *
   * Ohne dieses Gedächtnis bleibt der Zoom bei einer Fenstergrößenänderung
   * stehen, obwohl er für die alte Größe berechnet wurde — der Gast sieht dann
   * einen angeschnittenen Ausschnitt. Jede eigene Navigation verwirft es.
   */
  private fitted: { rect: Rect; padding: number; minZoom: number } | null = null
  /**
   * Zoomstufe, bei der die Ebene zuletzt gerastert wurde, plus der Nachlauf,
   * der sie nach der Bewegung neu rastern lässt — siehe `trackRasterScale`.
   */
  private lastZoom = 1
  private zooming = false
  private resharpen: ReturnType<typeof setTimeout> | null = null

  readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  getCamera(): Camera {
    return this.camera
  }

  getSize(): ViewportSize {
    return this.size
  }

  getViewport(): Rect {
    return cameraToViewport(this.camera, this.size)
  }

  attachLayer(layer: HTMLElement | null): void {
    this.clearResharpen()
    this.layer = layer
    // Eine frisch angehängte Ebene wird ohnehin bei der aktuellen Zoomstufe
    // gerastert — sie braucht keinen Nachlauf.
    this.lastZoom = this.camera.zoom
    this.applyTransform()
  }

  setSize(size: ViewportSize): void {
    this.size = size
    if (this.fitted) {
      // Ein eingepasstes Rechteck bleibt maßgeblich: nach einer Größenänderung
      // wird neu gerechnet, statt den für die alte Größe gültigen Zoom zu behalten.
      if (this.animation !== null) {
        // Mitten in einer Fahrt nicht hart umschalten, sondern kurz nachziehen —
        // sonst reißt eine Größenänderung die laufende Bewegung ab.
        const target = this.fitted
        this.animateTo(this.fit(target), 150)
        this.fitted = target
      } else {
        this.camera = this.fit(this.fitted)
      }
    }
    this.applyTransform()
    this.scheduleNotify()
  }

  setCamera(next: Camera, stopAnimation = true): void {
    // `stopAnimation === false` kommt nur aus dem Animationsschritt; nur eine
    // echte Nutzereingabe verwirft das eingepasste Rechteck.
    if (stopAnimation) {
      this.cancelAnimation()
      this.fitted = null
    }
    /*
     * Unbrauchbare Werte werden verworfen, statt sie zu übernehmen.
     *
     * Ein einziges NaN in der Kamera vergiftet alles Nachfolgende: Der Viewport
     * wird NaN, jeder Sichtbarkeitstest schlägt fehl, und das Board wirkt leer,
     * obwohl alle Daten da sind. Genau so ein Fall ist hier schon aufgetreten —
     * ein Rechenfehler in der Animation, der ohne diese Sperre erst drei
     * Schichten weiter als „Elemente verschwinden" auffiel.
     */
    if (!Number.isFinite(next.x) || !Number.isFinite(next.y) || !Number.isFinite(next.zoom)) {
      return
    }
    this.camera = { x: next.x, y: next.y, zoom: clampZoom(next.zoom) }
    this.applyTransform()
    this.scheduleNotify()
  }

  private fit(target: { rect: Rect; padding: number; minZoom: number }): Camera {
    return viewportToCamera(target.rect, this.size, target.padding, target.minZoom)
  }

  panByScreen(dx: number, dy: number): void {
    this.setCamera({
      x: this.camera.x - dx / this.camera.zoom,
      y: this.camera.y - dy / this.camera.zoom,
      zoom: this.camera.zoom,
    })
  }

  /**
   * Zoomt so, dass der Board-Punkt unter `anchor` (Bildschirmkoordinate) genau
   * dort liegen bleibt. Ohne diesen Anker "wandert" der Inhalt beim Zoomen weg
   * vom Mauszeiger und das Zoomen fühlt sich sofort falsch an.
   */
  zoomAt(anchor: Point, factor: number): void {
    const before = screenToBoard(anchor, this.camera, this.size)
    const zoom = clampZoom(this.camera.zoom * factor)
    const after = screenToBoard(anchor, { ...this.camera, zoom }, this.size)
    this.setCamera({
      x: this.camera.x + (before.x - after.x),
      y: this.camera.y + (before.y - after.y),
      zoom,
    })
  }

  screenToBoard(point: Point): Point {
    return screenToBoard(point, this.camera, this.size)
  }

  /**
   * Übernimmt einen fremden Viewport, ohne die untere Zoomgrenze anzuwenden —
   * für den Folgemodus und die Gast-Vorschau. Der Wert stammt hier nicht aus
   * einer Nutzereingabe, sondern aus der Ansicht eines anderen Teilnehmers;
   * ihn zu beschneiden würde den Ausschnitt verfälschen.
   */
  adoptViewport(rect: Rect, padding = 1, durationMs = 0): void {
    if (rect.w <= 0 || rect.h <= 0) return
    const target = { rect, padding, minZoom: 0 }
    if (durationMs > 0) {
      // Für gedrosselt eintreffende Ausschnitte (Gast-Vorschau): die Lücken
      // zwischen zwei Meldungen werden überbrückt, statt zu springen.
      this.animateTo(this.fit(target), durationMs)
      this.fitted = target
      return
    }
    this.cancelAnimation()
    this.fitted = target
    this.camera = this.fit(target)
    this.applyTransform()
    this.scheduleNotify()
  }

  /**
   * Fährt animiert auf ein Board-Rechteck; `padding` 1.1 = 10 % Luft ringsum.
   *
   * `minZoom = 0` hebt die untere Zoomgrenze auf — nötig, wenn das Rechteck von
   * außen kommt (Folgemodus) und vollständig sichtbar sein muss.
   */
  animateToRect(rect: Rect, padding = 1.1, durationMs = 600, minZoom = MIN_ZOOM): void {
    if (rect.w <= 0 || rect.h <= 0) return
    const target = { rect, padding, minZoom }
    this.animateTo(this.fit(target), durationMs)
    // Nach `animateTo`, weil das die vorherige Einpassung verwirft.
    this.fitted = target
  }

  animateTo(target: Camera, durationMs = 600): void {
    this.cancelAnimation()
    this.fitted = null

    // Dauer 0 heißt „sofort" — und nicht „durch null teilen".
    if (durationMs <= 0) {
      this.setCamera(target)
      return
    }

    const from = this.camera
    const start = performance.now()

    const step = (now: number): void => {
      /*
       * `now` ist der Zeitstempel des Frames, nicht der Moment des Aufrufs —
       * er kann vor `start` liegen, wenn der Frame schon lief. Ohne die untere
       * Begrenzung wird der Fortschritt negativ, und bei gleichem Start- und
       * Zielwert ergibt `0 · −∞` ein NaN, das in der Kamera hängen bleibt.
       */
      const t = Math.min(1, Math.max(0, (now - start) / durationMs))
      this.setCamera(lerpCamera(from, target, t), false)
      if (t < 1) {
        this.animation = requestAnimationFrame(step)
      } else {
        this.animation = null
      }
    }
    this.animation = requestAnimationFrame(step)
  }

  cancelAnimation(): void {
    if (this.animation !== null) {
      cancelAnimationFrame(this.animation)
      this.animation = null
    }
  }

  private applyTransform(): void {
    if (!this.layer) return
    const { x, y, zoom } = this.camera
    const tx = this.size.width / 2 - x * zoom
    const ty = this.size.height / 2 - y * zoom
    this.layer.style.transform = `translate3d(${tx}px, ${ty}px, 0) scale(${zoom})`
    // Über diese Variable rechnen Cursor, Auswahlrahmen und Anfasser die
    // Skalierung wieder heraus — rein in CSS, ohne Render pro Frame.
    this.layer.style.setProperty('--zoom', String(zoom))
    this.trackRasterScale(zoom)
  }

  /**
   * Sorgt dafür, dass die Ebene am Ende einer Bewegung in der jetzt gültigen
   * Zoomstufe neu gerastert wird.
   *
   * `will-change: transform` friert in Chromium die Rasterauflösung ein: Die
   * Ebene wird einmal gezeichnet und danach nur noch als Textur skaliert.
   * Während einer Fahrt ist das genau richtig — nichts wird neu gezeichnet,
   * die Bewegung bleibt flüssig. Steht das Bild aber still, ist es fatal: Wer
   * aus der Übersicht in einen Frame fährt — im Vortrag bei jedem Folienwechsel
   * —, sähe dauerhaft die hochskalierte Rasterung der Übersicht. Text und
   * Bilder wirken unscharf, obwohl die passende Auflösung längst geladen ist.
   *
   * Deshalb gilt der Hinweis nur, solange sich der Zoom ändert; kurz danach
   * wird er entfernt und der Compositor rastert neu. Eine eigene
   * Compositor-Ebene bleibt es durchgehend, dafür sorgt das `translate3d`.
   */
  private trackRasterScale(zoom: number): void {
    if (zoom === this.lastZoom) return
    this.lastZoom = zoom

    if (!this.zooming) {
      this.zooming = true
      this.layer?.style.setProperty('will-change', 'transform')
    }
    if (this.resharpen !== null) clearTimeout(this.resharpen)
    this.resharpen = setTimeout(() => {
      this.resharpen = null
      this.zooming = false
      this.layer?.style.removeProperty('will-change')
    }, RESHARPEN_DELAY_MS)
  }

  private clearResharpen(): void {
    if (this.resharpen !== null) {
      clearTimeout(this.resharpen)
      this.resharpen = null
    }
    this.zooming = false
    this.layer?.style.removeProperty('will-change')
  }

  /**
   * Benachrichtigungen werden pro Frame zusammengefasst. Ein Wheel-Event-Sturm
   * löst sonst dutzende Snapshot-Durchläufe pro Frame aus.
   */
  private scheduleNotify(): void {
    if (this.notifyScheduled) return
    this.notifyScheduled = true
    requestAnimationFrame(() => {
      this.notifyScheduled = false
      for (const listener of this.listeners) listener()
    })
  }
}
